import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Search.module.css";
import { CiSearch } from "react-icons/ci";
import { IoArrowBack, IoChatbubbleOutline } from "react-icons/io5";
import { BsChatDotsFill } from "react-icons/bs";
import { HiOutlineDocumentArrowUp } from "react-icons/hi2";
// Import analytics tracking functions
import { trackSearch, trackButtonClick, trackNavigation } from "../../utils/analytics";
import { usePlaceholders } from "./usePlaceholders";
import { useSearchQueries } from "./useSearchQueries";

function Search({
  queryphrase,
  setQueryphrase,
  handleAutocomplete,
  executeSearch,
  setShowChatBox,
  showChatBox,
  autoCompleteList,
  searchComplete,
  initialLoad,
  domainText,
  resumeRoute = "/resume",
}) {
  const navigate = useNavigate();
  const [showOptions, setShowOptions] = useState(false);
  const autocompleteRef = useRef(null);
  const { smartChatPlaceholder, searchPlaceholder } = usePlaceholders();
  const { frequentSearches, frequentPhrases } = useSearchQueries();
  const activeSuggestions = showChatBox ? frequentPhrases : frequentSearches;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSuggestionClick = (item) => {
    if (showChatBox) {
      setQueryphrase(item);
    } else {
      handleFrequentSearchClick(item);
    }
  };

  const handleOptionClickInternal = (value) => {
    // Track autocomplete selection
    trackButtonClick("Autocomplete Selection", "Search");
    trackSearch(value, 0); // 0 as placeholder, update with actual results count if available

    executeSearch(value);
    setShowOptions(false);
  };

  const handleResumeClick = () => {
    // Track resume page navigation
    trackNavigation("Resume Upload", "Search Bar");
    trackButtonClick("Agentic AI Search for CVs", "Search Bar");

    navigate(resumeRoute);
  };

  const handleFrequentSearchClick = (item) => {
    // Track frequent search click
    trackButtonClick(`Frequent Search: ${item}`, "Frequent Searches");
    trackSearch(item, 0); // Update with results count if available

    setQueryphrase(item);
    executeSearch(item);
    setShowOptions(false);
  };

  const handleSearchSubmit = (query) => {
    // Track search submission
    trackSearch(query, 0); // Update with actual results count if available
    trackButtonClick("Search Submit", showChatBox ? "Chat Mode" : "Normal Mode");

    executeSearch(query);
    setShowOptions(false);
  };

  const handleChatToggle = (isOpening) => {
    // Track chat mode toggle
    if (isOpening) {
      trackButtonClick("Open Smart Chat", "Search Bar");
    } else {
      trackButtonClick("Close Smart Chat", "Chat Header");
    }

    setShowChatBox(isOpening);
  };

  return (
    <>
      {/* Dynamic Suggestions (Frequent Searches / Smart Prompts) */}
      {initialLoad && activeSuggestions?.length > 0 && (
        <>
          <p className={styles["frequent-searches"]}>
            {showChatBox ? "Suggested Prompts" : "Frequent Searches"}
          </p>

          <div className={styles["frequent-search-container"]}>
            {activeSuggestions.map((item, index) => (
              <span
                key={index}
                className={styles["frequent-search"]}
                onClick={() => handleSuggestionClick(item)}
              >
                {showChatBox ? <IoChatbubbleOutline /> : <CiSearch />}
                {item}
              </span>
            ))}
          </div>
        </>
      )}

      {/* Search input with buttons */}
      {!showChatBox && (
        <div className={styles["search-container"]} ref={autocompleteRef}>
          <div className={styles["search-query-wrapper"]}>
            <CiSearch className={styles["search-icon"]} />

            <input
              type="text"
              placeholder={searchPlaceholder || "Job title, Keywords, or Company"}
              className={styles["search-input"]}
              value={queryphrase}
              onClick={() => setShowOptions(true)}
              onFocus={() => setShowOptions(true)}
              onChange={(e) => {
                setQueryphrase(e.target.value);
                handleAutocomplete(e);
                setShowOptions(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit(e.target.value);
                }
              }}
            />

            {/* Search button INSIDE the input wrapper */}
            <button
              type="button"
              className={styles["search-ai-button"]}
              onClick={() => handleSearchSubmit(queryphrase)}
            >
              Search
            </button>
          </div>

          {/* Chat icon button with tooltip */}
          <div className={styles["tooltip-wrapper"]}>
            <button className={styles["resume-button"]} onClick={() => handleChatToggle(true)}>
              <BsChatDotsFill />
              <span className={styles["resume-button-text"]}> Smart Chat</span>
            </button>
            <span className={styles["tooltip-text"]}>Converse & Search using AI</span>
          </div>

          {/* Resume upload button */}
          {domainText?.toLowerCase() === "breez" && (
            <button
              className={styles["resume-button"]}
              onClick={handleResumeClick}
              title="Upload Resume"
            >
              <HiOutlineDocumentArrowUp />
              <span className={styles["resume-button-text"]}>Agentic AI Search for CVs</span>
            </button>
          )}

          {/* Autocomplete Dropdown */}
          {/* {showOptions && autoCompleteList?.length > 0 && (
            <ul className={styles["autocomplete-dropdown"]}>
              {autoCompleteList.map((option) => (
                <li key={option.text} onClick={() => handleOptionClickInternal(option.text)}>
                  {option.text}
                </li>
              ))}
            </ul>
          )} */}
          {showOptions && autoCompleteList?.length > 0 && (
            <ul className={styles["autocomplete-dropdown"]}>
              {autoCompleteList.map((group) => (
                <li key={group.group} className={styles["group-wrapper"]}>
                  {/* Group title */}
                  <div className={styles["group-title"]}>{group.group}</div>

                  {/* Items */}
                  <ul className={styles["group-items"]}>
                    {group.items.map((text) => (
                      <li
                        key={text}
                        className={styles["group-item"]}
                        onClick={() => handleOptionClickInternal(text)}
                      >
                        {text}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* AI Text Search Bar */}
      {showChatBox && (
        <div className={styles["search-text"]}>
          <div className={styles["chat-header"]}>
            <span className={styles["back-arrow"]} onClick={() => handleChatToggle(false)}>
              <IoArrowBack />
            </span>
            <h3>Chat Assistant</h3>
          </div>

          <textarea
            className={styles["chat-textarea"]}
            placeholder={smartChatPlaceholder || "Job title, Keywords, or Company"}
            rows={4}
            value={queryphrase}
            onChange={(e) => setQueryphrase(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.shiftKey) {
                e.preventDefault();
                handleSearchSubmit(queryphrase);
              }
            }}
          />

          <div className={styles["chat-footer"]}>
            <p className={styles["instructions"]}>
              Press <strong>Enter</strong> for new line, <strong>Shift + Enter</strong> to send
            </p>

            <button
              className={styles["search-ai-button"]}
              onClick={() => handleSearchSubmit(queryphrase)}
            >
              Search
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Search;
